import { Alert, Box, Snackbar, styled, Typography } from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import React from 'react'

import { DataTable } from '@/components/UI/DataTable'
import { Paginator } from '@/components/UI/Paginator'
import SkeletonRow from '@/components/UI/Skeleton'
import { PlatformPermission } from '@/domain/permissions/rbac'
import { usePermissions } from '@/domain/permissions/usePermissions'
import { ErrorPage } from '@/pages/Error'
import { IKyc } from '@/types'
import { kycColumns } from './columns'
import { RefreshButton } from './RefreshButton'
import { useKyc } from './useKyc'

export const Kyc: React.FC = () => {
  const {
    userId,
    kycPage,
    setKycPage,
    query,
    refresh,
    refreshableKycId,
    toastError,
    clearToastError,
  } = useKyc()
  const { data, isLoading, isError } = query
  const { user } = useAuth0()
  const { hasPermission } = usePermissions(user?.['https://carringtonlabs.io/appRole'])
  const canManageKyc = hasPermission(PlatformPermission.ManageKYC)

  const Header = (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
      <Typography variant="h6">KYC</Typography>
      {canManageKyc && refreshableKycId && (
        <RefreshButton kycId={refreshableKycId} refresh={refresh} />
      )}
    </Box>
  )

  if (!userId || isLoading) {
    return (
      <>
        {Header}
        <SkeletonRow columns={kycColumns} />
      </>
    )
  }

  const kycs = data?.data ?? []
  const hasKycs = kycs.length > 0

  return (
    <>
      {Header}

      {isError ? (
        <ErrorPage
          title="Unable to Fetch KYC"
          message="We couldn't retrieve KYC data at the moment. Please check your internet connection or try again later. If the issue persists, contact administrator."
        />
      ) : hasKycs ? (
        <DataTable<IKyc> rows={kycs} columns={kycColumns} />
      ) : (
        <EmptyContainer>
          <Message>No KYC records found for this customer.</Message>
        </EmptyContainer>
      )}

      {!isError && Number(data?.pagination?.totalRecords ?? 0) > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          mt={2}
          py={1}
          sx={{
            border: '1px solid #E4E7EC',
            borderRadius: '8px',
          }}
        >
          <Paginator
            totalPages={Number(data?.pagination?.totalPages ?? 1)}
            currentPage={kycPage}
            onChange={setKycPage}
          />
        </Box>
      )}

      <Snackbar
        open={!!toastError}
        autoHideDuration={6000}
        onClose={clearToastError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={clearToastError} sx={{ width: '100%' }}>
          {toastError}
        </Alert>
      </Snackbar>
    </>
  )
}

const EmptyContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  border: '1px solid #E4E7EC',
  borderRadius: '8px',
  padding: '3rem 0',
})

const Message = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  maxWidth: 600,
  textAlign: 'center',
}))
